import { useEffect, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { atomWithImmer } from 'jotai-immer';
import { useTranslation } from 'react-i18next';
import { InputAdornment, Stack, Switch, TextField, Typography } from '@mui/material';

import { selectedDeviceSafeAtom } from '@/components/common/MapView';
import { alarmSettingAtom, postAlarmSettingAtom } from '@/interfaces/alarm';
import RoundButton from '@/components/common/RoundButton';
import ListItemWithDelete from '@/components/common/ListItemWithDelete';

import type { TextFieldProps } from '@mui/material';

type AlarmKeys = 'temp' | 'humi' | 'battery' | 'offline';

const enabledAlarmsAtom = atomWithImmer({
  temp: false,
  humi: false,
  battery: false,
  offline: false,
});
const thresholdsAtom = atomWithImmer({
  tempLow: '2',
  tempHigh: '8',
  humidLow: '1',
  humidHigh: '70',
  battery: '10',
  offline: '30',
});
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneNumberRegex = /^\d+$/;

const TextFieldWithUnit = (props: TextFieldProps & { unit: string }) => (
  <TextField
    {...props}
    size="small"
    slotProps={{
      input: {
        endAdornment: <InputAdornment position="end">{props.unit}</InputAdornment>,
        sx: {
          bgcolor: 'white',
          '&.Mui-disabled': { bgcolor: 'revert' },
        },
      },
    }}
  />
);

function isNotBetween(numStr: string, lowerLimit: number, upperLimit: number) {
  if (numStr === '') return true;

  const num = Number(numStr);
  return num < lowerLimit || num > upperLimit;
}

export default function DeviceDetailAlarmSettings() {
  const { isHumiditySupported } = useAtomValue(selectedDeviceSafeAtom);
  const { data: alarmSetting, refetch } = useAtomValue(alarmSettingAtom);
  const { data: isSuccess, mutate, isPending } = useAtomValue(postAlarmSettingAtom);

  const [enabledAlarms, setEnabledAlarms] = useAtom(enabledAlarmsAtom);
  const [thresholds, setThresholds] = useAtom(thresholdsAtom);
  const [emailInput, setEmailInput] = useState('');
  const [phoneNumberInput, setPhoneNumberInput] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>([]);
  const { t } = useTranslation();

  const isError = {
    tempLow: isNotBetween(thresholds.tempLow, -99, 99),
    tempHigh: isNotBetween(thresholds.tempHigh, -99, 99),
    humidLow: isNotBetween(thresholds.humidLow, 1, 99),
    humidHigh: isNotBetween(thresholds.humidHigh, 1, 99),
    battery: isNotBetween(thresholds.battery, 1, 99),
    offline: isNotBetween(thresholds.offline, 20, 720),
  };
  const helperText = {
    temp: t('Devices.message.tempLimit'),
    humid: t('Devices.message.humidLimit'),
    battery: t('Devices.message.lowBatteryLimit'),
    offline: t('Devices.message.offlineLimit'),
  };
  const allValid = Object.values(isError).every((e) => !e);
  const isEmailValid = emailRegex.test(emailInput);
  const isPhoneNumberValid = phoneNumberRegex.test(phoneNumberInput);

  useEffect(() => {
    if (!alarmSetting) return;
    setEnabledAlarms(alarmSetting.alarm);
    setThresholds({
      tempLow: alarmSetting.threshold.temp.min.toString(),
      tempHigh: alarmSetting.threshold.temp.max.toString(),
      humidLow: alarmSetting.threshold.humi.min.toString(),
      humidHigh: alarmSetting.threshold.humi.max.toString(),
      battery: alarmSetting.threshold.battery.toString(),
      offline: alarmSetting.threshold.offline.toString(),
    });
    setEmails(alarmSetting.receiver.email);
    setPhoneNumbers(alarmSetting.receiver.sms);
  }, [alarmSetting, setEnabledAlarms, setThresholds]);
  useEffect(() => {
    if (isSuccess === undefined) return;
    if (isSuccess) {
      refetch();
    } else {
      window.alert(t('Common.message.serverErrorOccurred'));
    }
  }, [isSuccess, refetch, t]);

  function onSwitchChange(key: AlarmKeys, value: boolean) {
    setEnabledAlarms((draft) => {
      draft[key] = value;
    });
  }
  function onThresholdChange(key: keyof typeof thresholds, rawValue: string) {
    let value = rawValue;
    const numberRegex = /^-?\d*$/;
    if (!numberRegex.test(value)) {
      value = value.replaceAll(/(?!^-)\D/g, '');
    }

    setThresholds((draft) => {
      draft[key] = value;
    });
  }
  function onAddEmailClick() {
    setEmails([...emails, emailInput]);
    setEmailInput('');
  }
  function onRemoveEmailClick(item: string) {
    setEmails(emails.filter((email) => email !== item));
  }
  function onAddPhoneNumberClick() {
    setPhoneNumbers([...phoneNumbers, phoneNumberInput]);
    setPhoneNumberInput('');
  }
  function onRemovePhoneNumberClick(item: string) {
    setPhoneNumbers(phoneNumbers.filter((phoneNumber) => phoneNumber !== item));
  }
  function onSaveClick() {
    const alarmSettingData = {
      alarm: enabledAlarms,
      threshold: {
        temp: { min: Number(thresholds.tempLow), max: Number(thresholds.tempHigh) },
        humi: { min: Number(thresholds.humidLow), max: Number(thresholds.humidHigh) },
        battery: Number(thresholds.battery),
        offline: Number(thresholds.offline),
      },
      receiver: {
        email: emails,
        sms: phoneNumbers,
      },
    };
    mutate(alarmSettingData);
  }

  // TODO: Hide setting while loading initial state
  return (
    <Stack gap={3}>
      <Typography variant="h5" fontWeight="bold">
        {t('Devices.label.alarm')}
      </Typography>

      <Stack gap={1}>
        <Stack gap={1} direction="row" alignItems="center">
          <Typography fontSize={20} fontWeight="bold">
            {t('Devices.label.temperature')}
          </Typography>
          <Switch checked={enabledAlarms.temp} onChange={(e) => onSwitchChange('temp', e.target.checked)} />
        </Stack>
        <Stack gap={2} direction="row" alignItems="center">
          <Typography sx={{ flexShrink: 0, mt: -3 }}>{t('Devices.label.threshold')}</Typography>
          <TextFieldWithUnit
            unit="℃"
            disabled={!enabledAlarms.temp}
            error={isError.tempLow}
            helperText={helperText.temp}
            value={thresholds.tempLow}
            onChange={(e) => onThresholdChange('tempLow', e.target.value)}
          />
          <Typography sx={{ mt: -3 }}> ~ </Typography>
          <TextFieldWithUnit
            unit="℃"
            disabled={!enabledAlarms.temp}
            error={isError.tempHigh}
            helperText={helperText.temp}
            value={thresholds.tempHigh}
            onChange={(e) => onThresholdChange('tempHigh', e.target.value)}
          />
        </Stack>
      </Stack>
      <Stack gap={1} sx={{ display: isHumiditySupported ? 'block' : 'none' }}>
        <Stack gap={1} direction="row" alignItems="center">
          <Typography fontSize={20} fontWeight="bold">
            {t('Devices.label.humidity')}
          </Typography>
          <Switch checked={enabledAlarms.humi} onChange={(e) => onSwitchChange('humi', e.target.checked)} />
        </Stack>
        <Stack gap={2} direction="row" alignItems="center">
          <Typography sx={{ flexShrink: 0, mt: -3 }}>{t('Devices.label.threshold')}</Typography>
          <TextFieldWithUnit
            unit="%"
            disabled={!enabledAlarms.humi}
            error={isError.humidLow}
            helperText={helperText.humid}
            value={thresholds.humidLow}
            onChange={(e) => onThresholdChange('humidLow', e.target.value)}
          />
          <Typography sx={{ mt: -3 }}> ~ </Typography>
          <TextFieldWithUnit
            unit="%"
            disabled={!enabledAlarms.humi}
            error={isError.humidHigh}
            helperText={helperText.humid}
            value={thresholds.humidHigh}
            onChange={(e) => onThresholdChange('humidHigh', e.target.value)}
          />
        </Stack>
      </Stack>
      <Stack gap={1}>
        <Stack gap={1} direction="row" alignItems="center">
          <Typography fontSize={20} fontWeight="bold">
            {t('Devices.label.battery')}
          </Typography>
          <Switch checked={enabledAlarms.battery} onChange={(e) => onSwitchChange('battery', e.target.checked)} />
        </Stack>
        <Stack gap={2} direction="row" alignItems="center">
          <Typography sx={{ flexShrink: 0, mt: -3 }}>{t('Devices.label.threshold')}</Typography>
          <TextFieldWithUnit
            unit="%"
            disabled={!enabledAlarms.battery}
            error={isError.battery}
            helperText={helperText.battery}
            value={thresholds.battery}
            onChange={(e) => onThresholdChange('battery', e.target.value)}
          />
        </Stack>
      </Stack>
      <Stack gap={1}>
        <Stack gap={1} direction="row" alignItems="center">
          <Typography fontSize={20} fontWeight="bold">
            {t('Devices.label.offline')}
          </Typography>
          <Switch checked={enabledAlarms.offline} onChange={(e) => onSwitchChange('offline', e.target.checked)} />
        </Stack>
        <Stack gap={2} direction="row" alignItems="center">
          <Typography sx={{ flexShrink: 0, mt: -3 }}>{t('Devices.label.threshold')}</Typography>
          <TextFieldWithUnit
            unit={t('Devices.label.minute')}
            disabled={!enabledAlarms.offline}
            error={isError.offline}
            helperText={helperText.offline}
            value={thresholds.offline}
            onChange={(e) => onThresholdChange('offline', e.target.value)}
          />
        </Stack>
      </Stack>

      <Stack gap={1}>
        <Typography fontSize={20} fontWeight="bold">
          {t('Devices.label.receiver')}
        </Typography>
        <Stack gap={2} direction="row" alignItems="center">
          <TextField
            fullWidth
            label={t('Settings.label.email')}
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            sx={{ bgcolor: 'white' }}
          />
          <RoundButton variant="contained" disabled={!isEmailValid} onClick={onAddEmailClick}>
            {t('Common.label.add')}
          </RoundButton>
        </Stack>
        {emails.map((email) => (
          <ListItemWithDelete key={email} label={email} onRemoveClick={() => onRemoveEmailClick(email)} />
        ))}

        <Stack gap={2} direction="row" alignItems="center">
          <TextField
            fullWidth
            label={t('Devices.label.phoneNumber')}
            value={phoneNumberInput}
            onChange={(e) => setPhoneNumberInput(e.target.value)}
            sx={{ bgcolor: 'white' }}
          />
          <RoundButton variant="contained" disabled={!isPhoneNumberValid} onClick={onAddPhoneNumberClick}>
            {t('Common.label.add')}
          </RoundButton>
        </Stack>
        {phoneNumbers.map((phoneNumber) => (
          <ListItemWithDelete
            key={phoneNumber}
            label={phoneNumber}
            onRemoveClick={() => onRemovePhoneNumberClick(phoneNumber)}
          />
        ))}
      </Stack>

      <RoundButton variant="contained" disabled={!allValid || isPending} onClick={onSaveClick} sx={{ mx: 2 }}>
        {t('Common.label.saveButton')}
      </RoundButton>
    </Stack>
  );
}
